import { useState, useRef, useEffect } from "react";
import { X, ChevronDown, Building2 } from "lucide-react";
import "./MultiSelectDepartments.css";

/**
 * Custom multi-select dropdown for departments
 * Clean, CRM-style UI with tag chips
 */
export default function MultiSelectDepartments({ 
  value = [], 
  onChange, 
  departments = [],
  required = false,
  disabled = false,
  placeholder = "Select departments...",
  className = "",
  hasError = false,
  testId = "multi-select-departments"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleToggleDepartment = (dept) => {
    if (disabled) return;
    
    const newValue = value.includes(dept)
      ? value.filter(d => d !== dept)
      : [...value, dept];
    
    onChange(newValue);
  };

  const handleRemoveDepartment = (e, dept) => {
    e.stopPropagation();
    if (disabled) return;
    
    const newValue = value.filter(d => d !== dept);
    onChange(newValue);
  };

  const getDeptTagClass = (dept) => {
    const d = (dept || "").toLowerCase().trim();
    if (d === "sales") return "dept-chip sales";
    if (d === "design") return "dept-chip design";
    if (d === "production") return "dept-chip production";
    if (d === "delivery") return "dept-chip delivery";
    if (d === "accounts") return "dept-chip accounts";
    if (d === "procurement") return "dept-chip procurement";
    if (d === "management") return "dept-chip management";
    return "dept-chip";
  };

  return (
    <div 
      ref={containerRef} 
      className={`multi-select-departments ${className}`}
      data-testid={testId}
    >
      <div 
        className={`multi-select-field ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${hasError ? "has-error" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        data-testid={`${testId}-trigger`}
      >
        <div className="multi-select-content">
          {value.length === 0 ? (
            <span className="multi-select-placeholder">{placeholder}</span>
          ) : (
            <div className="multi-select-tags">
              {value.map((dept) => (
                <span key={dept} className={getDeptTagClass(dept)}>
                  <Building2 size={10} />
                  {dept}
                  {!disabled && (
                    <button
                      type="button"
                      className="dept-chip-remove"
                      onClick={(e) => handleRemoveDepartment(e, dept)}
                      aria-label={`Remove ${dept}`}
                      data-testid={`${testId}-remove-${dept}`}
                    >
                      <X size={11} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronDown 
          size={16} 
          className="multi-select-chevron"
          style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
        />
      </div>

      {isOpen && (
        <div className="multi-select-dropdown" data-testid={`${testId}-dropdown`}>
          {departments.map((dept) => {
            const isSelected = value.includes(dept);
            return (
              <div
                key={dept}
                className={`multi-select-option ${isSelected ? "selected" : ""}`}
                onClick={() => handleToggleDepartment(dept)}
                data-testid={`${testId}-option-${dept}`}
              >
                <div className={`multi-select-checkbox ${isSelected ? "checked" : ""}`}>
                  {isSelected && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <Building2 size={13} style={{ opacity: 0.6 }} />
                <span>{dept}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
